import { ButtonNew, formatNumbers, Text } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import { ReactNode, useState } from 'react'

export type EmissionsBreakdownItem = {
    label: string
    value: number
    percentage: number
    color: string
    icon?: ReactNode
    order?: number
}

// percentage is a number 0-100
const formatPercentage = (percentage: number): string => {
    if (percentage > 0 && percentage < 1) {
        return '<1'
    }
    return Math.round(percentage).toString()
}

const EmissionsBreakdownTable = ({
    items,
    headerLeftLabel,
    headerRightLabel,
}: {
    items: EmissionsBreakdownItem[]
    headerLeftLabel: string
    headerRightLabel: string
}) => {
    const [showAll, setShowAll] = useState(false)

    const MAX_INITIAL_ITEMS = 6
    const hasMoreItems = items.length > MAX_INITIAL_ITEMS
    const displayItems = showAll ? items : items.slice(0, MAX_INITIAL_ITEMS)

    return (
        <Box
            sx={{
                width: '100%',
            }}
        >
            <Box
                sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pl: 3,
                    pr: 3,
                    backgroundColor: `Surface200`,
                }}
            >
                <Text variant={'body3-medium'} color={`TextSecondary`}>
                    {headerLeftLabel}
                </Text>
                <Text variant={'body3-medium'} color={`TextSecondary`}>
                    {headerRightLabel}
                </Text>
            </Box>
            <Box
                sx={{
                    width: '100%',
                }}
            >
                {displayItems.map((item, index) => (
                    <Box
                        key={`${item.label}-${index}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 2,
                            pl: 3,
                            pr: 3,
                            height: '56px',
                        }}
                    >
                        <Text
                            variant={'body3'}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 2,
                                minWidth: '145px',
                                width: '40%',
                            }}
                        >
                            {item.icon}
                            {item.label}
                        </Text>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                gap: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '12px',
                                    backgroundColor: 'Surface300',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${item.percentage}%`,
                                        height: '100%',
                                        backgroundColor: item.color,
                                        transition: 'width 0.3s ease',
                                        minWidth: item.percentage > 0 ? '2px' : 0,
                                    }}
                                />
                            </Box>
                            <Text
                                variant={'body3'}
                                sx={{
                                    minWidth: '30px',
                                    textAlign: 'right',
                                    color: `TextSecondary`,
                                }}
                            >
                                {formatPercentage(item.percentage)}%
                            </Text>
                        </Box>
                        <Text
                            variant={'body3'}
                            sx={{
                                width: '20%',
                                textAlign: 'right',
                                flex: '1 0 auto',
                            }}
                        >
                            {formatNumbers(item.value, 2)} tCO₂e
                        </Text>
                    </Box>
                ))}
            </Box>
            {hasMoreItems && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        pt: 2,
                        pb: 2,
                    }}
                >
                    <ButtonNew size={'sm'} style={'floating'} onClick={() => setShowAll(!showAll)}>
                        {showAll ? 'Show less' : 'Show more'}
                    </ButtonNew>
                </Box>
            )}
        </Box>
    )
}

export default EmissionsBreakdownTable
